import { content, type WorkEntry } from "../../data/content";

interface BrutalistEditorialProps {
  navigate: (to: string) => void;
}

function WorkText({ entry }: { entry: WorkEntry }) {
  return (
    <div>
      <h3 className="text-lg font-bold tracking-tight mb-3">{entry.title}</h3>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-400 mb-4">
        <span>{entry.location}</span>
        <span>{entry.dates}</span>
        <span>{entry.tools}</span>
      </div>
      <p className="text-sm leading-relaxed">{entry.description}</p>
      {entry.details && (
        <div className="mt-3 space-y-2">
          {entry.details.map((detail, i) => (
            <p key={i} className="text-sm leading-relaxed text-gray-600">
              {detail}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export function BrutalistEditorial({ navigate }: BrutalistEditorialProps) {
  const [marketplace, connectFour, nand] = content.work;

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      {/* Header */}
      <header className="bg-black text-white border-b border-black">
        <div className="max-w-7xl mx-auto px-6 py-8 md:py-10 flex items-center justify-between">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight">
            {content.name}
          </h1>
          <button
            onClick={() => navigate("/resume")}
            className="text-base border border-white/30 px-5 py-2.5 hover:bg-white hover:text-black transition-colors cursor-pointer"
          >
            Resume
          </button>
        </div>
      </header>

      {/* Welcome — photo + intro */}
      <section className="border-b border-black">
        <div className="flex flex-col md:flex-row">
          <div className="md:w-[340px] lg:w-[650px] shrink-0 border-b md:border-b-0 md:border-r border-black bg-black">
            <img
              src={content.images.restaurant}
              alt="Colin Kuzmick"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 flex flex-col">
            <div className="flex-1 flex flex-col justify-center p-8 md:p-12 lg:p-16">
              <p className="text-xl md:text-2xl font-light leading-relaxed">
                {content.welcome}
              </p>
              <div className="mt-8">
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-gray-400 block mb-4">
                  Contact Me
                </span>
                <div className="flex items-center gap-4">
                  <a
                    href="https://www.linkedin.com/in/colin-kuzmick-16b87a2b9/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-gray-900">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                  <a
                    href="https://github.com/Quzmick"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-gray-900">
                      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                    </svg>
                  </a>
                  <a
                    href="mailto:cmkuzmick@gmail.com"
                    className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-gray-900">
                      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
            <div className="flex items-end justify-end gap-4 pr-6 pb-4">
              <span className="text-base text-gray-500 mb-2">
                Blender 3d Modeling Summer Project -
              </span>
              <img
                src="/assets/corvette.png"
                alt="Corvette 3D model in Blender"
                className="w-48 lg:w-130"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Work heading */}
      <section className="bg-black text-white border-b border-black">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h2 className="text-xl font-bold uppercase tracking-[0.3em] text-center">
            Projects
          </h2>
        </div>
      </section>

      {/* Entry 1: image left, text right */}
      <div className="px-6 pb-24 pt-4">
        <section className="border-b border-black">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-1/2 shrink-0 border-b md:border-b-0 md:border-r border-black">
              <img
                src={marketplace!.image}
                alt={marketplace!.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="md:w-1/2 flex flex-col justify-center p-8 md:p-12 lg:p-16">
              <WorkText entry={marketplace!} />
            </div>
          </div>
        </section>

        {/* Entry 2: text left, image right */}
        <section className="border-b border-black">
          <div className="flex flex-col md:flex-row-reverse">
            <div className="md:w-1/2 shrink-0 border-b md:border-b-0 md:border-l border-black">
              <img
                src={connectFour!.image}
                alt={connectFour!.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="md:w-1/2 flex flex-col justify-center p-8 md:p-12 lg:p-16">
              <WorkText entry={connectFour!} />
            </div>
          </div>
        </section>

        {/* Entry 3: no image, text only */}
        <section className="border-b border-black">
          <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
            <WorkText entry={nand!} />
          </div>
        </section>
      </div>
    </div>
  );
}
